import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import axios from "axios";
import "./App.css";
import { Toaster } from "sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Login from "@/pages/Login";
import AdminLayout from "@/components/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCustomers from "@/pages/admin/AdminCustomers";
import ProductList from "@/pages/admin/ProductList";
import AddEditProduct from "@/pages/admin/AddEditProduct";
import AdminSettings from "@/pages/admin/AdminSettings";
import EmailNotifications from "@/pages/admin/EmailNotifications";
import HomepageBuilder from "@/pages/admin/HomepageBuilder";
import OrdersPage from "@/pages/admin/OrdersPage";
import InvoicePrint from "@/pages/admin/InvoicePrint";
import ShippingLabel from "@/pages/admin/ShippingLabel";
import CheckoutSettings from "@/pages/admin/CheckoutSettings";
import MobileAccountSettings from "@/pages/admin/MobileAccountSettings";
import CouponsPage from "@/pages/admin/CouponsPage";
import HomePage from "@/pages/HomePage";
import ProductPage from "@/pages/product/ProductPage";
import CartPage from "@/pages/CartPage";
import ReviewsPage from "@/pages/ReviewsPage";
import CustomerLogin from "@/pages/CustomerLogin";
import CategoryPage from "@/pages/CategoryPage";
import CheckoutPage from "@/pages/CheckoutPage";
import CategoryBanners from "@/pages/admin/CategoryBanners";
import PagesAdmin from "@/pages/admin/PagesAdmin";
import DemoSeeder from "@/pages/admin/DemoSeeder";
import AnalyticsPage from "@/pages/admin/AnalyticsPage";
import SubscriptionPlansAdmin from "@/pages/admin/SubscriptionPlansAdmin";
import CustomerSubscriptionsAdmin from "@/pages/admin/CustomerSubscriptionsAdmin";
import AbandonedCartAdmin from "@/pages/admin/AbandonedCartAdmin";
import SearchPage from "@/pages/SearchPage";
import OrderTrackingPage from "@/pages/OrderTrackingPage";
import WishlistPage from "@/pages/WishlistPage";
import AccountPage from "@/pages/AccountPage";
import StorefrontPage from "@/pages/StorefrontPage";
import AnalyticsTracker from "@/components/AnalyticsTracker";

const API = process.env.REACT_APP_BACKEND_URL;

/** Fetches brand_color from settings and injects it as --brand CSS variable */
function BrandColorInjector() {
  const { pathname } = useLocation();
  // Storefront pages get the brand-colour theme; the admin panel keeps its own palette
  useEffect(() => {
    const isAdmin = pathname.startsWith("/admin") || pathname === "/login" || pathname.startsWith("/invoice");
    document.documentElement.classList.toggle("storefront-theme", !isAdmin);
  }, [pathname]);

  useEffect(() => {
    axios.get(`${API}/api/settings`).then(r => {
      const color = r.data?.brand_color || "#e86f28";
      document.documentElement.style.setProperty("--brand", color);
      const darken = (hex, pct) => {
        const n = parseInt(hex.replace("#",""), 16);
        const f = 1 - pct / 100;
        const r2 = Math.round((n >> 16) * f), g = Math.round(((n >> 8) & 0xff) * f), b = Math.round((n & 0xff) * f);
        return `#${[r2,g,b].map(x=>x.toString(16).padStart(2,"0")).join("")}`;
      };
      document.documentElement.style.setProperty("--brand-dark", darken(color, 15));
      document.documentElement.style.setProperty("--brand-light", color + "18");
    }).catch(() => {});
  }, []);
  return null;
}

/** Syncs cart to backend for logged-in customers (enables abandoned cart emails) */
function CartSyncer() {
  useEffect(() => {
    const sync = () => {
      const token = localStorage.getItem("it_customer_token");
      if (!token) return;
      try {
        const items = JSON.parse(localStorage.getItem("it_cart") || "[]");
        axios.post(`${API}/api/customer/cart/sync`, { items }, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => {});
      } catch {}
    };
    sync(); // sync on mount
    window.addEventListener("it_cart_updated", sync);
    return () => window.removeEventListener("it_cart_updated", sync);
  }, []);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <BrandColorInjector />
        <CartSyncer />
        <AnalyticsTracker />
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/admin/login" element={<Navigate to="/login" replace />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="products" element={<ProductList />} />
            <Route path="products/new" element={<AddEditProduct />} />
            <Route path="products/:id" element={<AddEditProduct />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="checkout-settings" element={<CheckoutSettings />} />
            <Route path="mobile-account" element={<MobileAccountSettings />} />
            <Route path="coupons" element={<CouponsPage />} />
            <Route path="email-notifications" element={<EmailNotifications />} />
            <Route path="homepage" element={<HomepageBuilder />} />
            <Route path="orders" element={<OrdersPage />} />
          <Route path="invoice/:orderId" element={<InvoicePrint />} />
            <Route path="shipping-label/:orderId" element={<ShippingLabel />} />
            <Route path="categories" element={<CategoryBanners />} />
              <Route path="pages" element={<PagesAdmin />} />
            <Route path="analytics" element={<AnalyticsPage />} />
            <Route path="subscription-plans" element={<SubscriptionPlansAdmin />} />
            <Route path="customer-subscriptions" element={<CustomerSubscriptionsAdmin />} />
            <Route path="abandoned-cart" element={<AbandonedCartAdmin />} />
            <Route path="seed" element={<DemoSeeder />} />
          </Route>
          <Route path="/products/:slug" element={<ProductPage />} />
          <Route path="/cart" element={<CartPage />} />
          <Route path="/reviews/:slug" element={<ReviewsPage />} />
          <Route path="/customer-login" element={<CustomerLogin />} />
          <Route path="/c/:slug" element={<CategoryPage />} />
          <Route path="/checkout" element={<CheckoutPage />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/orders/:id" element={<OrderTrackingPage />} />
          <Route path="/wishlist" element={<WishlistPage />} />
          <Route path="/pages/:slug" element={<StorefrontPage />} />
          <Route path="/account" element={<AccountPage />} />
          <Route path="/" element={<HomePage />} />
          {/* Unknown URLs go home instead of rendering a blank page */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
